import { useState } from 'react';
import { cardLayout, initials, resolveTheme } from '../utils/theme';
import { useFittedLogo } from '../utils/logoFit';
import { BRAND } from '../config/brand';

const safeUrl = (url) => (/^https?:\/\//i.test(url || '') ? url : null);
const bareUrl = (url) => url.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
const telHref = (phone) => `tel:${phone.replace(/[^\d+]/g, '')}`;

/** Contact rows a card shows, in order, skipping empty ones. */
function contactItems(card) {
  const items = [];
  if (card.phone) items.push({ key: 'phone', label: 'Mobile', value: card.phone, href: telHref(card.phone), icon: 'phone' });
  if (card.email) items.push({ key: 'email', label: 'Email', value: card.email, href: `mailto:${card.email}`, icon: 'mail' });
  if (safeUrl(card.website)) items.push({ key: 'website', label: 'Website', value: bareUrl(card.website), href: card.website, icon: 'globe', external: true });
  if (safeUrl(card.linkedin)) items.push({ key: 'linkedin', label: 'LinkedIn', value: bareUrl(card.linkedin).replace(/^linkedin\.com/i, ''), href: card.linkedin, icon: 'linkedin', external: true });
  if (safeUrl(card.instagram)) items.push({ key: 'instagram', label: 'Instagram', value: bareUrl(card.instagram).replace(/^instagram\.com\//i, '@'), href: card.instagram, icon: 'instagram', external: true });
  if (safeUrl(card.twitter)) items.push({ key: 'twitter', label: 'X', value: bareUrl(card.twitter).replace(/^(x|twitter)\.com\//i, '@'), href: card.twitter, icon: 'x', external: true });
  return items;
}

const ICONS = {
  phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>,
  globe: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" /></>,
  linkedin: <><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></>,
  instagram: <><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><path d="M17.5 6.5h.01" /></>,
  x: <path d="M4 4l16 16M20 4 4 20" />,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  chevron: <path d="m9 18 6-6-6-6" />,
};

function Icon({ name, size = 20, color = 'currentColor', stroke = 1.75 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
      {ICONS[name]}
    </svg>
  );
}

/** The person's photo, or their initials when there is no photo. */
function Avatar({ card, size, t, ring = '#FFFFFF', ringWidth = 4, rounded = 'rounded-full', className = '', style = {} }) {
  const box = { width: size, height: size, ...(ringWidth ? { border: `${ringWidth}px solid ${ring}` } : {}), ...style };
  if (card.photo_url) {
    return <img src={card.photo_url} alt={card.full_name} className={`${rounded} object-cover shrink-0 ${className}`} style={box} />;
  }
  return (
    <div className={`${rounded} shrink-0 flex items-center justify-center font-display font-extrabold ${className}`}
      style={{ ...box, background: t.accentSoft, color: t.accent_color, fontSize: Math.round(size * 0.32) }}
      aria-hidden="true">
      {initials(card.full_name)}
    </div>
  );
}

/**
 * Company logo, or a lettered tile plus the company name.
 * The logo is fitted into `box` (px) by its real shape, after its empty margins are trimmed
 * (utils/logoFit): square/round marks get the full height, wide wordmarks the full width.
 */
function Brand({ card, t, onDark, size = 28, box = { w: 200, h: 52 } }) {
  const name = card.company || '';
  const logo = useFittedLogo(t.logo_url);
  const [natural, setNatural] = useState(null);
  if (t.logo_url) {
    const aspect = logo.aspect || natural || 1;
    // Round/square marks carry less detail per pixel than wordmarks: give them a bit more height
    const maxH = aspect < 1.6 ? box.h * 1.25 : box.h;
    const width = Math.round(Math.min(box.w, maxH * aspect));
    const height = Math.round(width / aspect);
    return (
      <span className={`inline-flex items-center max-w-full ${onDark ? 'rounded-xl bg-white px-2.5 py-2' : ''}`}>
        <img src={logo.src} alt={`${name || 'Company'} logo`} className="block object-contain max-w-full"
          style={{ width, height }}
          onLoad={(e) => { const { naturalWidth: w, naturalHeight: h } = e.currentTarget; if (w && h && !logo.aspect) setNatural(w / h); }} />
      </span>
    );
  }
  if (!name) return null;
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <span className="rounded-md flex items-center justify-center font-display font-extrabold shrink-0"
        style={{ width: size, height: size, fontSize: Math.round(size * 0.54), background: onDark ? '#FFFFFF' : t.primary_color, color: onDark ? t.primary_color : t.onPrimary }}>
        {name[0].toUpperCase()}
      </span>
      <span className="font-display font-bold text-base truncate">{name}</span>
    </span>
  );
}

function SaveButton({ onSave, saving, className, style, children }) {
  return (
    <button type="button" onClick={onSave} disabled={saving} tabIndex={onSave ? undefined : -1}
      className={`w-full font-semibold text-base flex items-center transition hover:opacity-90 disabled:opacity-70 ${className}`} style={style}>
      {children || (saving ? 'Preparing contact...' : 'Save contact')}
    </button>
  );
}

function PoweredBy({ dark }) {
  return (
    <a href="/" className={`flex items-center justify-center gap-2 text-[13px] ${dark ? 'text-white/70 hover:text-white' : 'text-ink-muted hover:text-ink'}`}>
      <img src={BRAND.logo.mark} alt="" className="h-[18px] w-auto" />
      Powered by {BRAND.name}
    </a>
  );
}

/** Lets long emails and links wrap after @ and dots instead of mid-word. */
function Breakable({ text }) {
  return text.split(/(?<=[@.])/).map((part, i) => <span key={i}>{i > 0 && <wbr />}{part}</span>);
}

const linkProps = (item) => (item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {});
const subtitle = (card) => [card.title].filter(Boolean).join('');
const companyLine = (card) => [card.company, card.department].filter(Boolean).join(' · ');

/* ---------------- Layouts ---------------- */

function Classic({ card, t, items, onSave, saving }) {
  return (
    <div className="flex flex-col min-h-full bg-white">
      <div className="h-40 shrink-0 px-5 pt-5" style={{ background: t.primary_color, color: t.onPrimary }}>
        <Brand card={card} t={t} onDark={t.onPrimary === '#FFFFFF'} box={{ w: 200, h: 46 }} />
      </div>
      <div className="flex-1 flex flex-col px-6 pb-5 gap-5">
        <Avatar card={card} t={t} size={112} className="-mt-14 shadow-lg" />
        <div>
          <h1 className="font-display font-extrabold text-[30px] leading-[34px] tracking-tight text-ink">{card.full_name}</h1>
          {subtitle(card) && <p className="text-base text-ink-muted mt-1">{subtitle(card)}</p>}
          {companyLine(card) && <p className="text-sm text-ink-muted">{companyLine(card)}</p>}
        </div>
        <SaveButton onSave={onSave} saving={saving} className="h-[52px] rounded-xl justify-center" style={{ background: t.accent_color, color: t.onAccent }} />
        {items.length > 0 && (
          <div className="flex flex-col border-t border-line">
            {items.map((it) => (
              <a key={it.key} href={it.href} {...linkProps(it)} className="flex items-center gap-3 py-3.5 border-b border-line text-ink hover:bg-surface -mx-2 px-2 rounded">
                <Icon name={it.icon} color={t.accent_color} />
                <span className="min-w-0">
                  <span className="block text-xs text-ink-muted">{it.label}</span>
                  <span className="block text-[15px] font-medium break-words">{it.value}</span>
                </span>
              </a>
            ))}
          </div>
        )}
        <div className="mt-auto pt-2"><PoweredBy /></div>
      </div>
    </div>
  );
}

function Banner({ card, t, items, onSave, saving }) {
  const quick = items.filter((i) => ['phone', 'email'].includes(i.key));
  const rest = items.filter((i) => !['phone', 'email'].includes(i.key));
  return (
    <div className="flex flex-col min-h-full bg-white">
      <div className="h-60 shrink-0 relative overflow-hidden" style={{ background: t.primary_color }}>
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 390 240" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <circle cx="330" cy="40" r="150" fill={t.accent_color} opacity="0.45" />
          <circle cx="60" cy="230" r="110" fill="#FFFFFF" opacity="0.12" />
          <path d="M250 200a90 90 0 0 1 90-90" fill="none" stroke="#FFFFFF" strokeWidth="5" strokeLinecap="round" opacity="0.3" />
          <path d="M250 200a130 130 0 0 1 130-130" fill="none" stroke="#FFFFFF" strokeWidth="5" strokeLinecap="round" opacity="0.2" />
        </svg>
        <div className={`absolute top-4 left-4 max-w-[calc(100%-32px)] bg-white flex items-center text-ink ${t.logo_url ? 'rounded-2xl px-3 py-2' : 'h-10 px-3 rounded-full'}`}>
          <Brand card={card} t={t} size={22} box={{ w: 200, h: 48 }} />
        </div>
      </div>
      <div className="flex-1 flex flex-col items-center text-center px-6 pb-5 gap-4">
        <Avatar card={card} t={t} size={124} ringWidth={5} className="-mt-[62px] shadow-xl relative z-10" />
        <div>
          <h1 className="font-display font-extrabold text-[32px] leading-[36px] tracking-tight text-ink">{card.full_name}</h1>
          {(subtitle(card) || card.company) && (
            <p className="text-base text-ink-muted mt-1">{[card.title, card.company].filter(Boolean).join(' · ')}</p>
          )}
        </div>
        <div className="flex gap-2.5 w-full">
          <SaveButton onSave={onSave} saving={saving} className="flex-1 h-[52px] rounded-full justify-center" style={{ background: t.accent_color, color: t.onAccent }} />
          {quick.map((it) => (
            <a key={it.key} href={it.href} aria-label={it.label} className="w-[52px] h-[52px] shrink-0 rounded-full border border-line-strong flex items-center justify-center text-ink hover:bg-surface">
              <Icon name={it.icon} />
            </a>
          ))}
        </div>
        {rest.length > 0 && (
          <div className="w-full flex flex-col gap-2 text-left">
            {rest.map((it) => (
              <a key={it.key} href={it.href} {...linkProps(it)} className="min-h-[56px] rounded-2xl bg-surface flex items-center gap-3 px-4 text-ink hover:bg-blue-soft">
                <Icon name={it.icon} color={t.accent_color} />
                <span className="flex-1 min-w-0 text-[15px] font-medium break-words">{it.value}</span>
                <Icon name="chevron" size={16} color="#4A5A6E" stroke={2} />
              </a>
            ))}
          </div>
        )}
        <div className="mt-auto pt-2"><PoweredBy /></div>
      </div>
    </div>
  );
}

function Split({ card, t, items, onSave, saving }) {
  return (
    <div className="flex flex-col min-h-full bg-surface px-4 pt-5 pb-4 gap-4">
      <div className="flex items-center text-ink"><Brand card={card} t={t} /></div>
      <div className="bg-white rounded-[20px] p-4 flex gap-4 items-stretch shadow-[0_1px_2px_rgba(11,21,48,0.06),0_12px_32px_rgba(11,21,48,0.10)]">
        <Avatar card={card} t={t} size={128} ringWidth={0} rounded="rounded-2xl" style={{ height: 160 }} />
        <div className="flex flex-col justify-center gap-1.5 min-w-0">
          <h1 className="font-display font-extrabold text-[26px] leading-[30px] tracking-tight text-ink break-words">{card.full_name}</h1>
          {subtitle(card) && <p className="text-[15px] leading-5 text-ink-muted">{subtitle(card)}</p>}
          {card.department && <p className="text-xs font-semibold tracking-wider uppercase text-ink-muted">{card.department}</p>}
        </div>
      </div>
      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-2.5">
          {items.map((it) => (
            <a key={it.key} href={it.href} {...linkProps(it)} className="bg-white rounded-2xl p-3.5 flex flex-col gap-2.5 border border-line text-ink hover:border-line-strong min-w-0">
              <Icon name={it.icon} size={22} color={t.accent_color} />
              <span className="min-w-0">
                <span className="block text-xs font-semibold tracking-wider uppercase text-ink-muted">{it.label}</span>
                <span className="block text-[13px] leading-[18px] font-medium [overflow-wrap:anywhere]"><Breakable text={it.value} /></span>
              </span>
            </a>
          ))}
        </div>
      )}
      <div className="mt-auto flex flex-col gap-2.5 pt-2">
        <SaveButton onSave={onSave} saving={saving} className="h-14 rounded-2xl justify-center shadow-lg" style={{ background: t.accent_color, color: t.onAccent }} />
        <PoweredBy />
      </div>
    </div>
  );
}

function Minimal({ card, t, items, onSave, saving }) {
  const [first, ...last] = (card.full_name || '').split(' ');
  return (
    <div className="flex flex-col min-h-full bg-white px-7 pt-8 pb-5 gap-7">
      <div className="flex items-center justify-between gap-4">
        {t.logo_url ? <Brand card={card} t={t} box={{ w: 190, h: 52 }} /> : (
          <span className="text-xs font-semibold tracking-[0.08em] uppercase text-ink-muted">{card.company}</span>
        )}
        <Avatar card={card} t={t} size={64} ringWidth={0} />
      </div>
      <div className="flex flex-col gap-3 mt-4">
        <h1 className="font-display font-extrabold text-[48px] leading-[48px] tracking-[-0.035em] text-ink break-words">
          {first}{last.length > 0 && <><br />{last.join(' ')}</>}
        </h1>
        {subtitle(card) && <p className="text-[17px] leading-6 text-ink-muted">{subtitle(card)}</p>}
      </div>
      {items.length > 0 && (
        <div className="flex flex-col">
          {items.map((it, i) => (
            <a key={it.key} href={it.href} {...linkProps(it)}
              className={`flex justify-between items-baseline gap-4 py-3.5 border-t text-ink hover:text-blue ${i === 0 ? 'border-ink' : 'border-line'} ${i === items.length - 1 ? 'border-b border-b-line' : ''}`}>
              <span className="text-[13px] text-ink-muted shrink-0">{it.label}</span>
              <span className="text-base font-medium text-right break-all">{it.value}</span>
            </a>
          ))}
        </div>
      )}
      <div className="mt-auto flex flex-col gap-3.5">
        <SaveButton onSave={onSave} saving={saving} className="h-[54px] rounded-xl justify-between px-5" style={{ background: t.primary_color, color: t.onPrimary }}>
          <span>{saving ? 'Preparing contact...' : 'Save contact'}</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-coral" /><Icon name="arrow" size={18} stroke={2} /></span>
        </SaveButton>
        <PoweredBy />
      </div>
    </div>
  );
}

function Bold({ card, t, items, onSave, saving }) {
  const fg = t.onPrimary;
  const dark = fg === '#FFFFFF';
  const quick = items.filter((i) => ['phone', 'email', 'website'].includes(i.key));
  const [first, ...last] = (card.full_name || '').split(' ');
  return (
    <div className="flex flex-col min-h-full px-6 pt-6 pb-5 gap-6 relative overflow-hidden" style={{ background: t.primary_color, color: fg }}>
      <svg width="300" height="300" viewBox="0 0 300 300" aria-hidden="true" className="absolute -right-[90px] top-[90px] pointer-events-none">
        <circle cx="120" cy="180" r="18" fill="#FF5E5B" />
        <path d="M120 130a50 50 0 0 1 50 50" fill="none" stroke="#FF5E5B" strokeWidth="7" strokeLinecap="round" />
        <path d="M120 96a84 84 0 0 1 84 84" fill="none" stroke="#FF5E5B" strokeWidth="7" strokeLinecap="round" opacity="0.6" />
        <path d="M120 62a118 118 0 0 1 118 118" fill="none" stroke="#FF5E5B" strokeWidth="7" strokeLinecap="round" opacity="0.3" />
      </svg>
      <div className="relative"><Brand card={card} t={t} onDark={dark} /></div>
      <div className="relative flex flex-col gap-3.5 mt-10">
        <Avatar card={card} t={t} size={92} ringWidth={3} ring={fg} />
        <h1 className="font-display font-extrabold text-[52px] leading-[50px] tracking-[-0.035em] break-words">
          {first}{last.length > 0 && <><br />{last.join(' ')}</>}
        </h1>
        {subtitle(card) && <p className="text-[17px] leading-6 opacity-80">{subtitle(card)}</p>}
      </div>
      {quick.length > 0 && (
        <div className="relative grid gap-2.5" style={{ gridTemplateColumns: `repeat(${quick.length}, minmax(0, 1fr))` }}>
          {quick.map((it) => (
            <a key={it.key} href={it.href} {...linkProps(it)} className="h-16 rounded-2xl flex flex-col items-center justify-center gap-1 text-[13px] hover:bg-white/10"
              style={{ border: `1px solid ${dark ? 'rgba(255,255,255,0.35)' : 'rgba(11,21,48,0.3)'}`, color: fg }}>
              <Icon name={it.icon} />{it.key === 'phone' ? 'Call' : it.label}
            </a>
          ))}
        </div>
      )}
      <div className="relative flex flex-col gap-2.5 text-[15px] leading-[22px]">
        {items.filter((i) => i.key !== 'website').map((it) => (
          <a key={it.key} href={it.href} {...linkProps(it)} className="break-words hover:underline" style={{ color: fg }}>
            <span className="opacity-70">{it.label} </span>{it.value}
          </a>
        ))}
      </div>
      <div className="relative mt-auto flex flex-col gap-3.5">
        <SaveButton onSave={onSave} saving={saving} className="h-14 rounded-full justify-center font-bold"
          style={{ background: dark ? '#FFFFFF' : '#0B1530', color: dark ? t.primary_color : '#FFFFFF' }} />
        <PoweredBy dark={dark} />
      </div>
    </div>
  );
}

const LAYOUT_COMPONENTS = { classic: Classic, banner: Banner, split: Split, minimal: Minimal, bold: Bold };

/**
 * A digital business card in one of five layouts. The layout comes from
 * `layout`, else the card's own choice, else the company default.
 * Shows the person's photo when they have one, otherwise their initials.
 */
export default function CardDisplay({ card, theme, layout, onSave, saving = false, className = '' }) {
  const t = resolveTheme(theme);
  const id = layout || cardLayout(card, theme);
  const Layout = LAYOUT_COMPONENTS[id] || Classic;
  return (
    <div className={`font-sans text-left ${className}`} data-layout={id}>
      <Layout card={card} t={t} items={contactItems(card)} onSave={onSave} saving={saving} />
    </div>
  );
}

export { Avatar };
